import { NonBreakingSpace, WrapperWidget } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { roundAndFormat } from '~/helper/number';
import { TrendingUp, TrendingDown, Home } from 'lucide-react';
import { cn } from '~/utils/cn';

interface Props {
  readonly evaluationValue?: number;
  readonly evaluationDate?: string;
  readonly trendPercent?: number;
  readonly previousValue?: number;
  readonly hidden?: boolean;
}

export function CockpitEvaluationTrendWidget(props: Props) {
  const { t } = useTranslate();

  const {
    evaluationValue,
    evaluationDate,
    trendPercent,
    previousValue,
    hidden,
  } = props;

  // Hidden flag from backend = no data available
  if (hidden || !evaluationValue) {
    return null;
  }

  const formattedValue = `CHF${NonBreakingSpace}${roundAndFormat(
    evaluationValue,
  )}`;

  const formattedDate = evaluationDate
    ? new Date(evaluationDate).toLocaleDateString('de-CH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const hasTrend = typeof trendPercent === 'number' && trendPercent !== 0;
  const isPositive = hasTrend && trendPercent > 0;
  const isNegative = hasTrend && trendPercent < 0;

  // Format previous value for tooltip/comparison
  const formattedPreviousValue = previousValue
    ? `CHF${NonBreakingSpace}${roundAndFormat(previousValue)}`
    : null;

  return (
    <WrapperWidget
      title={t('cockpit_evaluation_trend_widget_title') || 'Aktuelle Schätzung'}
    >
      <div className="h-full flex flex-col gap-y-4">
        {/* Main Value Section */}
        <div className="my-auto space-y-3">
          {/* Value + Trend Badge */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
            <span className="text-3xl font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {formattedValue}
            </span>

            {hasTrend && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium transition-colors',
                  isPositive &&
                    'border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
                  isNegative &&
                    'border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
                )}
              >
                {isPositive ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                <span>
                  {isPositive ? '+' : ''}
                  {trendPercent.toFixed(1)}%
                </span>
              </span>
            )}
          </div>

          {/* Date Label */}
          {formattedDate && (
            <p className="text-sm text-muted-foreground">
              {t('cockpit_evaluation_trend_widget_date_label') || 'Stand'}{' '}
              {formattedDate}
            </p>
          )}

          {/* Previous Value Comparison (if exists) */}
          {formattedPreviousValue && hasTrend && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('cockpit_evaluation_trend_widget_previous_label') ||
                'Vorherige Schätzung'}
              : {formattedPreviousValue}
            </p>
          )}
        </div>

        {/* Footer Info */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-gray-100 dark:border-gray-700">
          <Home className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            {t('cockpit_evaluation_trend_widget_footer_info') ||
              'Basierend auf Marktdaten & Immobilieneigenschaften'}
          </span>
        </div>
      </div>
    </WrapperWidget>
  );
}
